/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import Model, { Providers } from '@tripian/model';
import classes from './KiwiHotelInfoModal.scss';
import { getCurrencySymbol } from '../../currencySymbol';
import KiwiVisaBenefitsModal from '../KiwiVisaBenefitsModal/KiwiVisaBenefitsModal';
import KiwiSpecialOffersModal from '../KiwiSpecialOffersModal/KiwiSpecialOffersModal';
import KiwiHotelSearchBar, { KiwiCitySuggestion } from '../../KiwiHotelSearchBar/KiwiHotelSearchBar';
import KiwiRateDetailModal from '../KiwiRateDetailModal/KiwiRateDetailModal';
import KiwiRoomRateModal from '../KiwiRoomRateModal/KiwiRoomRateModal';
import KiwiBookingModal from '../KiwiBookingModal/KiwiBookingModal';

type TabKey = 'overview' | 'location' | 'amenities' | 'rooms' | 'contact';

interface IKiwiHotelInfoModal {
  hotel: Providers.Kiwi.KiwiSearchHotel;
  onClose: () => void;
  fetchPropertyDetail?: (code: string) => Promise<Providers.Kiwi.KiwiPropertyDetail>;
  fetchPropertyAvailability?: (code: string, request: Providers.Kiwi.KiwiPropertyAvailabilityRequest) => Promise<Providers.Kiwi.KiwiPropertyAvailabilityResponse>;
  availabilityRequest?: Providers.Kiwi.KiwiPropertyAvailabilityRequest;
  searchDestination?: string;
  searchCheckIn?: string;
  searchCheckOut?: string;
  searchAdults?: number;
  searchChildren?: number;
  searchCitySuggestions?: KiwiCitySuggestion[];
  onSearchDestinationChange?: (value: string) => void;
  onSearchCitySelect?: (city: KiwiCitySuggestion) => void;
  onSearchCheckInChange?: (value: string) => void;
  onSearchCheckOutChange?: (value: string) => void;
  onSearchAdultsChange?: (value: number) => void;
  onSearchChildrenChange?: (value: number) => void;
  onSearch?: () => void;
  onBookNow?: (hotel: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => void;
  onCreateReservation?: (req: Providers.Kiwi.KiwiCreateReservationRequest) => Promise<Providers.Kiwi.KiwiCreateReservationResponse>;
  onReservationCreated?: (data: Providers.Kiwi.KiwiReservationData, externalId: string) => void;
  generateExternalId?: () => string;
  isFavorite?: boolean;
  onFavoriteToggle?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  showVisaBadge?: boolean;
  dayBadgeText?: string;
  onDayBadgeRemove?: () => void;
  onShare?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  topOffset?: string;
  onRoomsTabView?: (hotel: Providers.Kiwi.KiwiSearchHotel) => void;
  onRoomAvailabilitySearch?: (hotel: Providers.Kiwi.KiwiSearchHotel, checkIn: string, checkOut: string, adults: number, children: number) => void;
  onRateDetailOpen?: (hotel: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => void;
  onSeeMoreDetails?: (hotel: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => void;
  onBookNowClick?: (hotel: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate) => void;
  onReservationFailed?: (hotel: Providers.Kiwi.KiwiSearchHotel, room: Providers.Kiwi.KiwiRoom, rate: Providers.Kiwi.KiwiRate, errorMessage: string) => void;
  t: (value: Model.TranslationKey) => string;
}

type RatingKey = Exclude<keyof Providers.Kiwi.KiwiRating, 'overallScore'>;

const RATING_LABELS: Array<{ key: RatingKey; tKey: Model.TranslationKey }> = [
  { key: 'service', tKey: 'kiwi.infoModal.rating.service' },
  { key: 'rooms', tKey: 'kiwi.infoModal.rating.rooms' },
  { key: 'location', tKey: 'kiwi.infoModal.rating.location' },
  { key: 'foodBeverage', tKey: 'kiwi.infoModal.rating.foodBeverage' },
  { key: 'spaFitness', tKey: 'kiwi.infoModal.rating.spaFitness' },
  { key: 'attentionToDetail', tKey: 'kiwi.infoModal.rating.attentionToDetail' },
  { key: 'generosityValue', tKey: 'kiwi.infoModal.rating.generosityValue' },
  { key: 'wowHipFactor', tKey: 'kiwi.infoModal.rating.wowFactor' },
  { key: 'architectureDesign', tKey: 'kiwi.infoModal.rating.architectureDesign' },
  { key: 'fitToPromise', tKey: 'kiwi.infoModal.rating.fitToPromise' },
];

const FALLBACK_VISA_BENEFITS: string[] = [
  'Best available rate guarantee',
  'Automatic room upgrade upon arrival, when available',
  'Complimentary in-room Wi-Fi, when available',
  'Complimentary breakfast for two',
  '$25 USD food or beverage credit',
  'VIP Guest status',
  'Late check-out upon request, when available',
];

const DESCRIPTION_TRUNCATE_LENGTH = 380;

const TABS: Array<{ key: TabKey; tKey: Model.TranslationKey }> = [
  { key: 'overview', tKey: 'kiwi.infoModal.tab.overview' },
  { key: 'location', tKey: 'kiwi.infoModal.tab.location' },
  { key: 'amenities', tKey: 'kiwi.infoModal.tab.amenities' },
  { key: 'rooms', tKey: 'kiwi.infoModal.tab.rooms' },
  { key: 'contact', tKey: 'kiwi.infoModal.tab.contactUs' },
];

const KiwiHotelInfoModal: React.FC<IKiwiHotelInfoModal> = ({
  hotel,
  onClose,
  fetchPropertyDetail,
  fetchPropertyAvailability,
  availabilityRequest,
  searchDestination = '',
  searchCheckIn = '',
  searchCheckOut = '',
  searchAdults = 2,
  searchChildren = 0,
  searchCitySuggestions,
  onSearchDestinationChange,
  onSearchCitySelect,
  onSearchCheckInChange,
  onSearchCheckOutChange,
  onSearchAdultsChange,
  onSearchChildrenChange,
  onSearch,
  onBookNow,
  onCreateReservation,
  onReservationCreated,
  generateExternalId,
  isFavorite,
  onFavoriteToggle,
  showVisaBadge,
  dayBadgeText,
  onDayBadgeRemove,
  onShare,
  topOffset = '0px',
  onRoomsTabView,
  onRoomAvailabilitySearch,
  onRateDetailOpen,
  onSeeMoreDetails,
  onBookNowClick,
  onReservationFailed,
  t,
}) => {
  const [propertyDetail, setPropertyDetail] = useState<Providers.Kiwi.KiwiPropertyDetail | undefined>(undefined);
  const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
  const [availabilityData, setAvailabilityData] = useState<Providers.Kiwi.KiwiAvailabilityData | undefined>(undefined);
  const [loadingAvailability, setLoadingAvailability] = useState<boolean>(false);
  const [expandedRoomDescKeys, setExpandedRoomDescKeys] = useState<Set<string>>(new Set());
  const [rateDetailInfo, setRateDetailInfo] = useState<{ rate: Providers.Kiwi.KiwiRate; currency: string } | undefined>(undefined);
  const [roomRateModal, setRoomRateModal] = useState<{ roomTitle: string; rate: Providers.Kiwi.KiwiRate; currency: string } | undefined>(undefined);
  const [bookingModal, setBookingModal] = useState<{ room: Providers.Kiwi.KiwiRoom; rate: Providers.Kiwi.KiwiRate; currency: string } | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const [internalDestination, setInternalDestination] = useState(searchDestination);
  const [internalCheckIn, setInternalCheckIn] = useState(searchCheckIn || availabilityRequest?.in_date || '');
  const [internalCheckOut, setInternalCheckOut] = useState(searchCheckOut || availabilityRequest?.out_date || '');
  const [internalAdults, setInternalAdults] = useState(searchAdults || availabilityRequest?.adults || 2);
  const [internalChildren, setInternalChildren] = useState(searchChildren || availabilityRequest?.children || 0);
  const [descriptionExpanded, setDescriptionExpanded] = useState<boolean>(false);
  const [photosOpen, setPhotosOpen] = useState<boolean>(false);
  const [photoIndex, setPhotoIndex] = useState<number>(0);
  const [mobileTouchStartX, setMobileTouchStartX] = useState<number | null>(null);
  const [visaModalOpen, setVisaModalOpen] = useState(false);
  const [offersModalOpen, setOffersModalOpen] = useState(false);
  const mapRef = useRef<HTMLDivElement>(null);

  /* fetch full property detail when modal opens */
  useEffect(() => {
    let cancelled = false;
    if (fetchPropertyDetail && hotel.code) {
      setLoadingDetail(true);
      fetchPropertyDetail(hotel.code)
        .then((detail) => {
          if (!cancelled) setPropertyDetail(detail);
        })
        .catch(() => {
          /* fallback to search-level hotel data */
        })
        .finally(() => {
          if (!cancelled) setLoadingDetail(false);
        });
    }
    return () => {
      cancelled = true;
    };
  }, [fetchPropertyDetail, hotel.code]);

  /* initialize Google Map when location tab is active */
  useEffect(() => {
    if (activeTab !== 'location') return;
    if (!hotel.latitude || !hotel.longitude) return;
    const timer = setTimeout(() => {
      if (!mapRef.current) return;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const g = (window as any).google;
      if (!g?.maps) return;
      const lat = parseFloat(hotel.latitude);
      const lng = parseFloat(hotel.longitude);
      const map = new g.maps.Map(mapRef.current, {
        center: { lat, lng },
        zoom: 15,
        disableDefaultUI: true,
        zoomControl: true,
        gestureHandling: 'cooperative',
      });
      new g.maps.Marker({ position: { lat, lng }, map });
    }, 50);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [activeTab, hotel.latitude, hotel.longitude]);

  const triggerAvailabilityFetch = (checkIn: string, checkOut: string, adults: number, children: number) => {
    if (!fetchPropertyAvailability || !hotel.code || !checkIn || !checkOut) return;
    if (onRoomAvailabilitySearch) onRoomAvailabilitySearch(hotel, checkIn, checkOut, adults, children);
    setLoadingAvailability(true);
    setAvailabilityData(undefined);
    fetchPropertyAvailability(hotel.code, { in_date: checkIn, out_date: checkOut, adults, children })
      .then((res) => setAvailabilityData(res.data.availability ?? undefined))
      .catch(() => {})
      .finally(() => setLoadingAvailability(false));
  };

  /* auto-fetch when rooms tab is activated with pre-filled dates */
  useEffect(() => {
    if (activeTab !== 'rooms') return;
    if (onRoomsTabView) onRoomsTabView(hotel);
    const checkIn = availabilityRequest?.in_date || internalCheckIn;
    const checkOut = availabilityRequest?.out_date || internalCheckOut;
    const adults = availabilityRequest?.adults ?? internalAdults;
    const children = availabilityRequest?.children ?? internalChildren;
    triggerAvailabilityFetch(checkIn, checkOut, adults, children);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleRoomsSearch = () => {
    if (onSearch) onSearch();
    triggerAvailabilityFetch(internalCheckIn, internalCheckOut, internalAdults, internalChildren);
  };

  /* close photo gallery with ESC */
  useEffect(() => {
    if (!photosOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPhotosOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [photosOpen]);

  const data = propertyDetail || hotel;
  const starCount = Math.min(5, Math.round(parseFloat(data.starRating) || 0));
  const kiwiRating = propertyDetail?.kiwiRating;
  const overallScore = parseFloat(kiwiRating?.overallScore || data.kiwiOverallScore) || 0;
  const minRate = data.lowestNightRate || data.minimumRate;
  const price = minRate ? parseFloat(minRate) : 0;
  const currency = data.currencyCode || 'USD';
  const locationText = [data.city, data.country].filter(Boolean).join(', ');
  const localRating = propertyDetail?.localRating;
  const hasSpecialOffer = data.hasSpecialOffer === 'true';
  const imageUrls = useMemo<string[]>(() => {
    /* union of detail + search image sources, deduped by URL — protects against
       backends returning fewer/duplicate images at the detail endpoint */
    const seen = new Set<string>();
    const urls: string[] = [];
    const mainImage = propertyDetail?.mainImageUrl || hotel.mainImageUrl;
    if (mainImage) {
      urls.push(mainImage);
      seen.add(mainImage);
    }
    const sources = [propertyDetail?.images, hotel.images];
    sources.forEach((list) => {
      if (!list) return;
      list.forEach((img) => {
        if (img.url && !seen.has(img.url)) {
          urls.push(img.url);
          seen.add(img.url);
        }
      });
    });
    return urls;
  }, [propertyDetail, hotel.mainImageUrl, hotel.images]);

  const heroImage = imageUrls[0];
  const sideImages = imageUrls.slice(1, 5);

  const description = data.description || data.dreamText || '';
  const isLongDescription = description.length > DESCRIPTION_TRUNCATE_LENGTH;
  const visibleDescription = !isLongDescription || descriptionExpanded ? description : `${description.slice(0, DESCRIPTION_TRUNCATE_LENGTH)}…`;

  const roomCount = data.numberOfRooms;
  const settings = (data.settings || []).join(', ');
  const styles = (data.styles || []).join(' · ');

  /* benefit groups: prefer API-provided, fall back to hardcoded VISA list when showVisaBadge is enabled */
  const benefitGroups: Providers.Kiwi.KiwiPropertyBenefitGroup[] = useMemo(() => {
    if (propertyDetail?.benefitGroups && propertyDetail.benefitGroups.length > 0) {
      return propertyDetail.benefitGroups;
    }
    if (showVisaBadge) {
      return [{ title: 'VISA', benefits: FALLBACK_VISA_BENEFITS }];
    }
    return [];
  }, [propertyDetail?.benefitGroups, showVisaBadge]);

  const handleFindRoom = () => setActiveTab('rooms');

  const formattedPrice = price > 0 ? new Intl.NumberFormat('en-US').format(Math.round(price)) : '';

  const nightCount = useMemo(() => {
    if (!internalCheckIn || !internalCheckOut) return 0;
    const d1 = new Date(internalCheckIn);
    const d2 = new Date(internalCheckOut);
    return Math.max(0, Math.round((d2.getTime() - d1.getTime()) / (1000 * 60 * 60 * 24)));
  }, [internalCheckIn, internalCheckOut]);

  const getCurrencyName = (code: string) => {
    try {
      return new Intl.DisplayNames(['en'], { type: 'currency' }).of(code) || code;
    } catch {
      return code;
    }
  };

  const goToPrevPhoto = () => setPhotoIndex((prev) => (prev - 1 + imageUrls.length) % imageUrls.length);
  const goToNextPhoto = () => setPhotoIndex((prev) => (prev + 1) % imageUrls.length);

  return ReactDOM.createPortal(
    <div className={classes.overlay} onClick={onClose} role="presentation" style={{ top: topOffset, height: `calc(100% - ${topOffset})` }}>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div className={classes.panel} onClick={(e) => e.stopPropagation()}>
        {/* Mobile hero: full-width carousel with overlaid controls */}
        {imageUrls.length > 0 && (
          <div className={classes.mobileHero}>
            {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
            <div
              className={classes.mobileCarousel}
              onTouchStart={(e) => setMobileTouchStartX(e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (mobileTouchStartX === null) return;
                const dx = e.changedTouches[0].clientX - mobileTouchStartX;
                if (dx < -50) goToNextPhoto();
                else if (dx > 50) goToPrevPhoto();
                setMobileTouchStartX(null);
              }}
            >
              <img src={imageUrls[photoIndex]} alt={data.title} className={classes.mobileCarouselImg} loading="lazy" />
              <button type="button" className={classes.mobileCloseBtn} onClick={onClose} aria-label={t('kiwi.common.close')}>
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
              <div className={classes.mobileTopRight}>
                <button type="button" className={classes.mobileIconBtn} onClick={() => onFavoriteToggle?.(hotel)} aria-label={t('kiwi.infoModal.favorite')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill={isFavorite ? '#e11d48' : 'none'} stroke={isFavorite ? '#e11d48' : '#111827'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                  </svg>
                </button>
                <button type="button" className={classes.mobileIconBtn} onClick={() => onShare?.(hotel)} aria-label={t('kiwi.infoModal.share')}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                    <polyline points="16 6 12 2 8 6" />
                    <line x1="12" y1="2" x2="12" y2="15" />
                  </svg>
                </button>
              </div>
              {imageUrls.length > 1 && (() => {
                const MAX_DOTS = 5;
                const total = imageUrls.length;
                const count = Math.min(MAX_DOTS, total);
                const half = Math.floor(MAX_DOTS / 2);
                const start = Math.max(0, Math.min(photoIndex - half, total - MAX_DOTS));
                return (
                  <div className={classes.mobileCarouselDots}>
                    {Array.from({ length: count }, (_, i) => {
                      const realIdx = start + i;
                      const isActive = realIdx === photoIndex;
                      const isEdge = (i === 0 && start > 0) || (i === count - 1 && start + count < total);
                      return (
                        <span
                          key={realIdx}
                          className={[classes.mobileCarouselDot, isActive ? classes.mobileCarouselDotActive : '', isEdge ? classes.mobileCarouselDotSmall : ''].filter(Boolean).join(' ')}
                        />
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Close row */}
        <div className={classes.closeRow}>
          <button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t('kiwi.common.close')}>
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {/* Title row */}
        <div className={classes.titleRow}>
          <h2 className={classes.hotelName}>{data.title}</h2>
          <div className={classes.headerActions}>
            {onShare && (
              <button type="button" className={classes.iconBtn} onClick={() => onShare(hotel)} aria-label={t('kiwi.infoModal.share')}>
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
                  <polyline points="16 6 12 2 8 6" />
                  <line x1="12" y1="2" x2="12" y2="15" />
                </svg>
              </button>
            )}
            {onFavoriteToggle && (
              <button type="button" className={classes.iconBtn} onClick={() => onFavoriteToggle(hotel)} aria-label={t('kiwi.infoModal.favorite')}>
                <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill={isFavorite ? '#e11d48' : 'none'} stroke={isFavorite ? '#e11d48' : '#111827'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Location */}
        {locationText && <p className={classes.hotelLocation}>{locationText}</p>}

        {/* Score row */}
        <div className={classes.scoreRow}>
          <div className={classes.scoreBadge}>
            <svg className={classes.scoreStar} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
            </svg>
            <span className={classes.scoreNum}>{overallScore > 0 ? overallScore.toFixed(1) : '—'}</span>
          </div>
          {starCount > 0 && (
            <>
              <span className={classes.scoreDot}>·</span>
              <span className={classes.goldStars}>
                {Array.from({ length: starCount }, (_, i) => (
                  <svg key={i} className={classes.goldStar} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                ))}
              </span>
            </>
          )}
        </div>

        {/* Image grid */}
        {heroImage && (
          <div className={classes.imageGrid}>
            <div
              className={classes.imageMain}
              onClick={() => {
                setPhotoIndex(0);
                setPhotosOpen(true);
              }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  setPhotoIndex(0);
                  setPhotosOpen(true);
                }
              }}
            >
              <img src={heroImage} alt={data.title} className={classes.gridImg} loading="lazy" />
            </div>
            <div className={classes.imageSide}>
              {Array.from({ length: 4 }, (_, idx) => {
                const url = sideImages[idx];
                if (!url) {
                  return <div key={`empty-${idx}`} className={`${classes.imageThumb} ${classes.imageThumbPlaceholder}`} />;
                }
                return (
                  <div
                    key={`thumb-${idx}-${url}`}
                    className={classes.imageThumb}
                    onClick={() => {
                      setPhotoIndex(idx + 1);
                      setPhotosOpen(true);
                    }}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setPhotoIndex(idx + 1);
                        setPhotosOpen(true);
                      }
                    }}
                  >
                    <img src={url} alt={`${data.title} ${idx + 2}`} className={classes.gridImg} loading="lazy" />
                  </div>
                );
              })}
            </div>
            {imageUrls.length > 1 && (
              <button
                type="button"
                className={classes.showAllPhotos}
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotoIndex(0);
                  setPhotosOpen(true);
                }}
              >
                {t('kiwi.infoModal.showAllPhotos')}
              </button>
            )}
          </div>
        )}

        {/* Price + {t('kiwi.infoModal.findRoom')} block */}
        <div className={classes.priceBlock}>
          <div className={classes.priceLeft}>
            {showVisaBadge && (
              <span
                className={classes.visaBadge}
                onClick={(e) => {
                  e.stopPropagation();
                  setVisaModalOpen(true);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={() => {}}
                aria-label={t('kiwi.card.visaPremiumBenefits')}
              />
            )}
            {hasSpecialOffer && (
              <span
                className={classes.specialOffersBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  setOffersModalOpen(true);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={() => {}}
                aria-label={t('kiwi.common.specialOffers')}
              />
            )}
          </div>

          <div className={classes.priceRight}>
            {formattedPrice && (
              <div className={classes.priceValue}>
                <span className={classes.priceSymbol}>{getCurrencySymbol(currency)}</span>
                <span className={classes.priceAmount}>{formattedPrice}</span>
                <span className={classes.priceUnit}> {currency}/{t('kiwi.common.night')}</span>
              </div>
            )}
            <button type="button" className={classes.findRoomBtn} onClick={handleFindRoom}>
              {t('kiwi.infoModal.findRoom')}
            </button>
          </div>
        </div>

        {/* Mobile find a room — full-width button below price block */}
        <div className={classes.mobileFindRoomRow}>
          <button type="button" className={classes.mobileFindRoomBtn} onClick={handleFindRoom}>
            {t('kiwi.infoModal.findRoom')}
          </button>
        </div>

        {/* Tabs */}
        <div className={classes.tabs}>
          {TABS.map((tab) => (
            <button key={tab.key} type="button" className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ''}`} onClick={() => setActiveTab(tab.key)}>
              {t(tab.tKey)}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className={classes.tabContent}>
          {activeTab === 'overview' && (
            <div className={classes.overview}>
              {/* Quick info */}
              <div className={classes.quickInfo}>
                {roomCount && (
                  <div className={classes.quickRow}>
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" className={classes.quickIcon}>
                      <rect width="22" height="22" fill="url(#icon-rooms)" />
                      <defs>
                        <pattern id="icon-rooms" patternContentUnits="objectBoundingBox" width="1" height="1">
                          {/* eslint-disable-next-line react/no-unknown-property */}
                          <use xlinkHref="#icon-rooms-img" transform="scale(0.01)" />
                        </pattern>
                        {/* eslint-disable-next-line react/no-unknown-property */}
                        <image
                          id="icon-rooms-img"
                          width="100"
                          height="100"
                          preserveAspectRatio="none"
                          xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAAEL0lEQVR4nO3cz4scRRQH8Le4m52pmuzsq35vJprREY344yJGb+JNED14EFRU8JYFjf+CCt4VxNPmpOSkCP4APYgXox7UCB4U9CBJREVXDZgI/oiovO6K0UCYZcN0v+75fqAOe9juev3tru7pqhkiAAAAAAAAAAAAAAAAAIC6LfeL4pZ+UdwdWA4Elg0n7UDZJ5GbrY+dPy0GKd0QWV4ISX+KSf/23IL1keX5QVFcRx20K7A8F5P+ea5g+TSyvhaTHopJN520Q9anwPrZf8I5E1iftRqoC4bDIYck7+bifrfiVoejq8i5tTW9Op9Ef+Qr5h2rhVpuObC8XYUhx8O63kgtE4ZyU0x6ogyF5a1W31si61NlGKzf9FPaSy3VT2kSWb/NQ+0T1EZB5NLI8ktM+teA9VZqucG63Ga1BJbTQXUPtU1I8mQee1+kjggsL+er5HFqm5jkk+reMbqdOiKw3pGH4I+pVabTnl3ekeVXIlqh7tgVk/xmtRHtW6W2sMfa/GT1JXVMTHLMalsdjq+ktggi+9t5ac9mNZX3EZH91Bb2LigHcpQ6JrIezYHY+652QCDOIBBnEIgzCMQZBNLSQHrMl9vriMB6auaEEeupyPLK7qK4drv9mMf2O/uUVR6sHcwchqQn7X9n9WFe2+9sIGdf1O1wevWlmX2Y0/Y7HMjsYSReeHj5eWYf5rT9zgay04MVc5vVh3ltv9WB2Hhs8+khyZYtFrjYgxSba2eshqoWPdmmQJYiy/3nrdroZAtWI8t9VjN5ZNOaMemb5zotxwPrMzZBVb6qnk571FbTac9qsFqqmqpFD7m9EUejMXmyW+QaC6A6c+T7AesjHZuYOt/KYF0ftVrzyXdsTXUfedAvistsVUnZMdYP7W9aEP2U9kbWj3LtX9vCjqb7tBySvF/dwOU9mkz6tGgmk/7/jgHRJY31JRZ6MI+jJ+J4PKIFFUejcUz6lR2LPFw3dmb8WHYi6T204AZJ781XyVYjDzAh6cN57PzA7aNfvZbsHloek/XioZr3XX4Sfr3ceaEHa9+5UzHpY9VJKq/Wve+ls29St/PmdVH0mK/Iw9YPtY4a+Sa2rZd8iyawnC7vq4M9Wu83oapAPq9tpy0RWL+oHnTS9fXttMPrri5WI2+DEciFIRBnEIgzCMQZBOIMAnEGgTiDQJxBIM4gEGcQiDMIxBkE4gwCcQaBONNIIJigcjZBlRfIbf27trX5n+bbdNLKtc2B5bvaF8wFlrtsTr3pVejRWbNjEljupCbYakVbIFb+xGqSw9WKCz3i4OdeN2ppSY/kVSaH7e9ysZyXFZzWoXyWbNKCiNUwZUPUBnmDQJxBIM4gEGcQiDMIxJnIxYP5sfdpWhCh+gKorXZ/gBxasa8Jd+H30bfLarXPHh3/gisAAAAAAAAAAAAAAAAAUF3+Aas4nOGFV3mWAAAAAElFTkSuQmCC"
                        />
                      </defs>
                    </svg>
                    <span className={classes.quickLabel}>{t('kiwi.common.roomsSuites')}</span>
                    <span className={classes.quickValue}>{roomCount}</span>
                  </div>
                )}
                {settings && (
                  <div className={classes.quickRow}>
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" className={classes.quickIcon}>
                      <rect width="22" height="22" fill="url(#icon-setting)" />
                      <defs>
                        <pattern id="icon-setting" patternContentUnits="objectBoundingBox" width="1" height="1">
                          {/* eslint-disable-next-line react/no-unknown-property */}
                          <use xlinkHref="#icon-setting-img" transform="scale(0.01)" />
                        </pattern>
                        {/* eslint-disable-next-line react/no-unknown-property */}
                        <image
                          id="icon-setting-img"
                          width="100"
                          height="100"
                          preserveAspectRatio="none"
                          xlinkHref="data:image/png;base64,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"
                        />
                      </defs>
                    </svg>
                    <span className={classes.quickLabel}>{t('kiwi.common.setting')}</span>
                    <span className={classes.quickValue}>{settings}</span>
                  </div>
                )}
                {styles && (
                  <div className={classes.quickRow}>
                    {/* eslint-disable-next-line react/no-unknown-property */}
                    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" xmlnsXlink="http://www.w3.org/1999/xlink" className={classes.quickIcon}>
                      <rect width="22" height="22" fill="url(#icon-style)" />
                      <defs>
                        <pattern id="icon-style" patternContentUnits="objectBoundingBox" width="1" height="1">
                          {/* eslint-disable-next-line react/no-unknown-property */}
                          <use xlinkHref="#icon-style-img" transform="scale(0.01)" />
                        </pattern>
                        {/* eslint-disable-next-line react/no-unknown-property */}
                        <image
                          id="icon-style-img"
                          width="100"
                          height="100"
                          preserveAspectRatio="none"
                          xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAADNUlEQVR4nO2ZTWsTURSGp/WjzX3TpPfMuQlBJIIKguBCEFy4cyn+Bf9C6UqXLt26dOtfEBcuxG8qCIJudCOKKCIiIopf1Sp3Mi1DBGs1kzmZvA9cCJk09/Z9MmfuR5IQQgghhBBCCCGEEELGAtLOcSf6MLb4mrFXhFPtwetFSFiDhJ+Fdmne+90UMz62Q3TZ+fA+E+D1o/PhTGzxdXxvcE2X42cppkSaPhxzog+Kd8Ncu7tn/XpDZFd+12TXnQ+PWcZKoNVqifPhPCT8GIStT5zXE398rvjwKBezlknqdjtljG3amHUSTjnRN3m4XyF6Lun35//ib3c6H07D6+fsbpHwDqJLSZJsG8O464dTPQyvK4Xyc7WZpge2+j2tVtgLCZcLZe5+QzpHyxl1DWm324t5efo+eGiHF/Eu+d/vbSymJyH6tFjGms2ejmbU9WQmK09eX+ehfYti0jRdGFkPvZ6DD2ch+iUvY2/zMjY7sj7qABY7h5zo7Y3yJOE6fPdgWf0tqO6H1ysbZcyHe400PVJWf5NDt4v4kIaE1XxN8SovTzPj6H5QxsLzXEycwV3w3reTaWQojNVYnkSkVcmPIitjcQY3/h9F5VgtFxhz2ayeyXigzpQ+sbBAPuV8NilTzvbvU++Xo5h6V86kL8rciBan1dPvz2flqR7bFrND2zdZGYuTgWQSqOvGXmuLG5yVMy1b381NjgAssCOWI+f1w/qBUSxXcbc1qfch2VLhkOzT4H/eN1f1wBJ4vTt0hDq9zetK1T4S58O1yoMQG81JuFG1D0LIPzN8S2/6eZ9t8hVqcjY5mLg+zGIxLFCIrbBAIbbCAoXYCgsUYissUIitsEAhtsIChdgKCxRiKyxQiK2wQCG2wgKF2AoLFGIrLFCIrbBAIbbCAoXYCgsUYissUIitsEAhtsIChdgKCxRiKyzUXYgTvTM84GltTvSWBSG3qg4CRpqTcLNqH4QQQgghhBBCOlyZB1srd67MA1fuhBBCCCGEEEIIIYQQQgghhJCkHH4BQxHq21Gtu/0AAAAASUVORK5CYII="
                        />
                      </defs>
                    </svg>
                    <span className={classes.quickLabel}>{t('kiwi.common.style')}</span>
                    <span className={classes.quickValue}>{styles}</span>
                  </div>
                )}
              </div>

              {/* Description */}
              {description && (
                <div className={classes.descriptionBlock}>
                  <p className={classes.description}>{visibleDescription}</p>
                  {isLongDescription && (
                    <button type="button" className={classes.readMore} onClick={() => setDescriptionExpanded((prev) => !prev)}>
                      {descriptionExpanded ? t('kiwi.infoModal.readLess') : t('kiwi.infoModal.readMore')}
                    </button>
                  )}
                </div>
              )}

              {/* Average Rating */}
              <div className={classes.section}>
                <div className={classes.sectionHeader}>
                  <svg className={classes.sectionIcon} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                  <span className={classes.sectionTitle}>{t('kiwi.infoModal.averageRating')}</span>
                </div>

                <div className={classes.averageOverall}>
                  <div className={classes.averageCircle}>{overallScore > 0 ? overallScore.toFixed(1) : '—'}</div>
                  {localRating && (localRating.source || localRating.value) && (
                    <div className={classes.localRating}>
                      {localRating.source && <span className={classes.localRatingSource}>{localRating.source}</span>}
                      {localRating.value && <span className={classes.localRatingValue}>{localRating.value}</span>}
                    </div>
                  )}
                </div>

                {kiwiRating ? (
                  <div className={classes.ratingGrid}>
                    {RATING_LABELS.map(({ key, tKey }) => {
                      const value = parseFloat(kiwiRating[key]) || 0;
                      const pct = Math.max(0, Math.min(100, (value / 10) * 100));
                      return (
                        <div key={key} className={classes.ratingRow}>
                          <div className={classes.ratingHead}>
                            <span className={classes.ratingLabel}>{t(tKey)}</span>
                            <span className={classes.ratingValue}>{value}</span>
                          </div>
                          <div className={classes.ratingBar}>
                            <div className={classes.ratingFill} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : loadingDetail ? (
                  <div className={classes.loadingHint}>{t('kiwi.infoModal.loadingRatings')}</div>
                ) : null}
              </div>

              {/* Benefit groups (VISA, etc.) */}
              {benefitGroups.map((group) => (
                <div key={group.title} className={classes.section}>
                  <div className={classes.sectionHeader}>
                    {group.title === 'VISA' ? (
                      <span className={classes.visaCircle} aria-label="Visa">
                      </span>
                    ) : (
                      <svg className={`${classes.sectionIcon} ${classes.sectionIconPrimary}`} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="currentColor">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                      </svg>
                    )}
                    <span className={classes.sectionTitle}>{group.title === 'VISA' ? t('kiwi.card.visaPremiumBenefits') : `${group.title} Benefits`}</span>
                  </div>
                  <ul className={classes.benefitsList}>
                    {group.benefits.map((benefit) => (
                      <li key={benefit} className={classes.benefitItem}>
                        <svg className={classes.checkSimple} width="16" height="16" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {activeTab === 'location' && (
            <div className={classes.locationTab}>
              {/* Address section */}
              <div className={classes.locationSection}>
                <div className={classes.locationSectionHeader}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={classes.locationIcon}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#0B1215" />
                  </svg>
                  <span className={classes.locationSectionLabel}>{t('kiwi.infoModal.address')}</span>
                </div>
                <div className={classes.locationAddressBlock}>
                  {data.addressLine1 && <span className={classes.locationAddressLine}>{data.addressLine1}</span>}
                  {data.addressLine2 && <span className={classes.locationAddressLine}>{data.addressLine2}</span>}
                  {data.addressLine3 && <span className={classes.locationAddressLine}>{data.addressLine3}</span>}
                  {(data.city || data.country) && (
                    <span className={classes.locationAddressLine}>{[data.city, data.country].filter(Boolean).join(', ')}</span>
                  )}
                  {data.zipCode && <span className={classes.locationAddressLine}>{data.zipCode}</span>}
                </div>
              </div>

              {/* Google Map + Get directions overlay */}
              {data.latitude && data.longitude && (
                <div className={classes.locationMapWrapper}>
                  <div ref={mapRef} className={classes.locationMapDiv} />
                  <button
                    type="button"
                    className={classes.locationDirectionsBtn}
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${data.latitude},${data.longitude}`, '_blank', 'noopener,noreferrer')}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                      <path d="M21.71 11.29l-9-9a.996.996 0 0 0-1.41 0l-9 9a.996.996 0 0 0 0 1.41l9 9c.39.39 1.02.39 1.41 0l9-9a.996.996 0 0 0 0-1.41zM14 14.5V12h-4v3H8v-4c0-.55.45-1 1-1h5V7.5l3.5 3.5-3.5 3.5z" />
                    </svg>
                    {t('kiwi.infoModal.getDirections')}
                  </button>
                </div>
              )}

              {/* Divider */}
              {data.locationDescription && <div className={classes.locationDivider} />}

              {/* Information section */}
              {data.locationDescription && (
                <div className={classes.locationSection}>
                  <div className={classes.locationSectionHeader}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className={classes.locationIcon}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" fill="#0B1215" />
                    </svg>
                    <span className={classes.locationSectionLabel}>{t('kiwi.infoModal.locationInfo')}</span>
                  </div>
                  <p className={classes.locationDescription}>{data.locationDescription}</p>
                </div>
              )}
            </div>
          )}
          {activeTab === 'amenities' && (
            <div>
              <div className={classes.amenitiesTitle}>{t('kiwi.includes')}</div>
              <div className={classes.amenitiesGrid}>
                {(propertyDetail?.facilities?.length ? propertyDetail.facilities : data.amenities || []).map((item) => (
                  <div key={item} className={classes.amenityItem}>
                    <svg className={classes.amenityCheck} viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    <span>{item.replace(/^Includes\s+/i, '')}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {activeTab === 'rooms' && (
            <div className={classes.roomsTab}>
              <div className={classes.roomsSearchBarWrapper}>
                <KiwiHotelSearchBar
                  destination={internalDestination}
                  checkInDate={internalCheckIn}
                  checkOutDate={internalCheckOut}
                  adults={internalAdults}
                  children={internalChildren}
                  citySuggestions={searchCitySuggestions}
                  onDestinationChange={(v) => { setInternalDestination(v); if (onSearchDestinationChange) onSearchDestinationChange(v); }}
                  onCitySelect={(c) => { if (onSearchCitySelect) onSearchCitySelect(c); }}
                  onCheckInChange={(v) => { setInternalCheckIn(v); if (onSearchCheckInChange) onSearchCheckInChange(v); }}
                  onCheckOutChange={(v) => { setInternalCheckOut(v); if (onSearchCheckOutChange) onSearchCheckOutChange(v); }}
                  onAdultsChange={(v) => { setInternalAdults(v); if (onSearchAdultsChange) onSearchAdultsChange(v); }}
                  onChildrenChange={(v) => { setInternalChildren(v); if (onSearchChildrenChange) onSearchChildrenChange(v); }}
                  onSearch={handleRoomsSearch}
                  t={t}
                />
              </div>
              {(loadingAvailability || availabilityData !== undefined) && (
                <div className={classes.roomsTopBar}>
                  <div className={classes.currencyRow}>
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="12" cy="12" r="10" stroke="#374151" strokeWidth="1.5" />
                      <path d="M12 6v1.5M12 16.5V18M9 9.5C9 8.12 10.34 7 12 7s3 1.12 3 2.5c0 1.5-1.5 2-3 2.5s-3 1-3 2.5C9 15.88 10.34 17 12 17s3-1.12 3-2.5" stroke="#374151" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                    <span>{currency} - {getCurrencyName(currency)}</span>
                  </div>
                </div>
              )}
              {loadingAvailability ? (
                <div className={classes.roomsLoading}>
                  <div className={classes.roomsSpinner}>
                    <svg viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
                      <circle cx="25" cy="25" r="20" />
                    </svg>
                  </div>
                  <div className={classes.roomsLoadingText}>
                    <p className={classes.roomsLoadingTitle}>{t('kiwi.infoModal.checkingAvailability')}</p>
                    {internalCheckIn && internalCheckOut && (
                      <p className={classes.roomsLoadingDates}>
                        {new Date(internalCheckIn).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        {' - '}
                        {new Date(internalCheckOut).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </p>
                    )}
                  </div>
                </div>
              ) : !availabilityData || availabilityData.rooms.length === 0 ? (
                availabilityData !== undefined ? (
                  <div className={classes.roomsEmpty}>
                    <p className={classes.roomsEmptyTitle}>{t('kiwi.infoModal.noRooms.title')}</p>
                    <p className={classes.roomsEmptySubtitle}>{t('kiwi.infoModal.noRooms.tryOptions')}</p>
                    <ol className={classes.roomsEmptyList}>
                      <li>{t('kiwi.infoModal.noRooms.changeDates')}</li>
                      <li>
                        In some cases we may be able to find available rooms.
                        <br />
                        {t('kiwi.infoModal.noRooms.callAgent')} <strong>1-800-953-7392</strong> {t('kiwi.common.or')}{' '}
                        <button type="button" className={classes.roomsEmptyLink} onClick={() => window.open('https://www.visasignaturehotels.com/contact/rate', '_blank', 'noopener,noreferrer')}>
                          {t('kiwi.infoModal.noRooms.contactUs')}
                        </button>{' '}
                        {t('kiwi.infoModal.noRooms.forAssistance')}.
                      </li>
                      <li>
                        <button type="button" className={classes.roomsEmptyLink} onClick={() => window.open(`https://www.visasignaturehotels.com/booking/request/${hotel.code}`, '_blank', 'noopener,noreferrer')}>
                          {t('kiwi.infoModal.noRooms.sendRequest')}
                        </button>{' '}
                        {t('kiwi.infoModal.noRooms.requestDates')} {data.title}.
                      </li>
                    </ol>
                  </div>
                ) : (
                  <p className={classes.roomsHint}>{t('kiwi.infoModal.noRooms.enterDates')}</p>
                )
              ) : (
                <>
                  {availabilityData.rooms.map((room) => {
                    const roomImg = imageUrls[0] || '';

                    const roomDescExpanded = expandedRoomDescKeys.has(room.code);
                    const ROOM_DESC_MAX = 140;
                    const isLongRoomDesc = (room.description || '').length > ROOM_DESC_MAX;
                    const visibleRoomDesc = !isLongRoomDesc || roomDescExpanded
                      ? room.description
                      : `${room.description.slice(0, ROOM_DESC_MAX)}`;

                    return (
                      <div key={room.code} className={classes.roomCard}>
                        {/* Room header: image + info */}
                        <div className={classes.roomCardTop}>
                          <div className={classes.roomImageCol}>
                            {roomImg && <img src={roomImg} alt={room.title} loading="lazy" />}
                          </div>
                          <div className={classes.roomInfoCol}>
                            <h4 className={classes.roomTitle}>{room.title}</h4>
                            {room.description && (
                              <p className={classes.roomDesc}>
                                {visibleRoomDesc}
                                {!roomDescExpanded && isLongRoomDesc && (
                                  <>
                                    {'… '}
                                    <button
                                      type="button"
                                      className={classes.roomReadMore}
                                      onClick={() => setExpandedRoomDescKeys((prev) => { const next = new Set(prev); next.add(room.code); return next; })}
                                    >
                                      {t('kiwi.infoModal.readMore')}
                                    </button>
                                  </>
                                )}
                              </p>
                            )}
                            {roomDescExpanded && (
                              <>
                                {room.sizeM2 && (
                                  <p className={classes.roomSize}>
                                    <strong>{t('kiwi.infoModal.roomSize')}:</strong> {room.sizeM2} {t('kiwi.infoModal.squareMeters')}{room.sizeFt2 ? ` | ${room.sizeFt2} ${t('kiwi.infoModal.squareFeet')}` : ''}
                                  </p>
                                )}
                                <button
                                  type="button"
                                  className={classes.roomReadMore}
                                  onClick={() => setExpandedRoomDescKeys((prev) => { const next = new Set(prev); next.delete(room.code); return next; })}
                                >
                                  {t('kiwi.common.close')}
                                </button>
                              </>
                            )}
                            <p className={classes.roomDisclaimer}>{t('kiwi.infoModal.roomDisclaimer')}</p>
                          </div>
                        </div>

                        {/* Rates */}
                        <div className={classes.rateList}>
                          {(room.rates || []).map((rate) => {
                            const nightlyPrice = parseFloat(rate.price?.inclusiveNightlyAverage) || 0;
                            const rateCurrency = rate.price?.currencyCode || currency;

                            const titleParts = (rate.title || '').split(' | ');
                            const rateTitleMain = titleParts[0] || '';
                            const rateTitleSub = titleParts.slice(1).join(' | ');

                            type PolicyItem = { text: string; type: 'bold' | 'info' | 'regular' };
                            const policyItems: PolicyItem[] = [];
                            if (rate.policies?.cancellation) policyItems.push({ text: rate.policies.cancellation, type: 'bold' });
                            if (rate.specialOfferRate === 'true') policyItems.push({ text: 'Includes Premium Benefits', type: 'info' });
                            if (rate.policies?.guarantee) policyItems.push({ text: rate.policies.guarantee, type: 'regular' });
                            if (rate.policies?.tax) policyItems.push({ text: rate.policies.tax, type: 'regular' });
                            if (rate.policies?.serviceCharges) policyItems.push({ text: rate.policies.serviceCharges, type: 'regular' });
                            rate.amenities?.forEach((amenity) => policyItems.push({ text: amenity, type: 'regular' }));

                            return (
                              <div key={rate.code} className={classes.rateCard}>
                                <div className={classes.rateTopRow}>
                                  <div className={classes.rateTitleBlock}>
                                    <span className={classes.rateTitleMain}>{rateTitleMain}</span>
                                    {rateTitleSub && (
                                      <>
                                        <span className={classes.rateTitleSep}> | </span>
                                        <span className={classes.rateTitleSub}>{rateTitleSub}</span>
                                      </>
                                    )}
                                    {policyItems.length > 0 && (
                                      <ul className={classes.ratePolicies}>
                                        {policyItems.map((policy, pi) => (
                                          // eslint-disable-next-line react/no-array-index-key
                                          <li key={pi} className={classes.policyItem}>
                                            {policy.type === 'info' ? (
                                              <svg className={classes.policyBulletIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="12" cy="12" r="10" />
                                                <line x1="12" y1="16" x2="12" y2="12" />
                                                <line x1="12" y1="8" x2="12.01" y2="8" />
                                              </svg>
                                            ) : (
                                              <span className={classes.policyBullet} aria-hidden="true">
                                                •
                                              </span>
                                            )}
                                            <span className={policy.type === 'bold' ? classes.policyTextBold : undefined}>{policy.text}</span>
                                          </li>
                                        ))}
                                      </ul>
                                    )}
                                  </div>
                                  <div className={classes.ratePriceCol}>
                                    {nightlyPrice > 0 && (
                                      <>
                                        <span className={classes.ratePriceAmount}>
                                          {getCurrencySymbol(rateCurrency)}
                                          {new Intl.NumberFormat('en-US').format(Math.round(nightlyPrice))} {rateCurrency}
                                        </span>
                                        <button type="button" className={classes.ratePriceLabelRow} onClick={() => { setRateDetailInfo({ rate, currency: rateCurrency }); if (onRateDetailOpen) onRateDetailOpen(hotel, room, rate); }}>
                                          <span>{t('kiwi.infoModal.avgNightlyRate')}</span>
                                          <svg className={classes.ratePriceInfoIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="12" cy="12" r="10" />
                                            <line x1="12" y1="16" x2="12" y2="12" />
                                            <line x1="12" y1="8" x2="12.01" y2="8" />
                                          </svg>
                                        </button>
                                      </>
                                    )}
                                    {hotel.catalogUrl && (
                                      <button
                                        type="button"
                                        className={classes.bookNowBtn}
                                        onClick={() => {
                                          if (onBookNowClick) onBookNowClick(hotel, room, rate);
                                          const base = hotel.catalogUrl.replace(/\/$/, '');
                                          const url = internalCheckIn && internalCheckOut
                                            ? `${base}/${internalCheckIn}/${internalCheckOut}/${internalAdults}/${internalChildren}/1`
                                            : base;
                                          window.open(url, '_blank', 'noopener,noreferrer');
                                        }}
                                      >
                                        {t('kiwi.common.bookNow')}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {(rate.description || (rate.policies && Object.values(rate.policies).some(Boolean))) && (
                                  <button type="button" className={classes.seeMoreBtn} onClick={() => { setRoomRateModal({ roomTitle: room.title, rate, currency: rateCurrency }); if (onSeeMoreDetails) onSeeMoreDetails(hotel, room, rate); }}>
                                    <span className={classes.seeMoreDesktop}>{t('kiwi.infoModal.seeMoreDetails')}</span>
                                    <span className={classes.seeMoreMobile}>{t('kiwi.infoModal.moreInfo')}</span>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          )}
          {activeTab === 'contact' && (
            <div className={classes.contactTab}>
              <h3 className={classes.contactTitle}>{t('kiwi.infoModal.contact.title')}</h3>
              <p className={classes.contactDesc}>{t('kiwi.infoModal.contact.description')}</p>

              <div className={classes.contactSection}>
                <div className={classes.contactSectionHeader}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={classes.contactIcon}>
                    <g clipPath="url(#clip-phone)">
                      <path d="M14.6665 11.2807V13.2807C14.6672 13.4664 14.6292 13.6502 14.5548 13.8203C14.4804 13.9904 14.3713 14.1431 14.2345 14.2686C14.0977 14.3941 13.9362 14.4897 13.7603 14.5492C13.5844 14.6087 13.398 14.6308 13.2131 14.614C11.1617 14.3911 9.19113 13.6901 7.45979 12.5674C5.84901 11.5438 4.48335 10.1782 3.45979 8.56738C2.33311 6.82819 1.63195 4.84805 1.41313 2.78738C1.39647 2.60303 1.41838 2.41722 1.47746 2.2418C1.53654 2.06637 1.63151 1.90518 1.7563 1.76846C1.8811 1.63175 2.033 1.52252 2.20232 1.44773C2.37164 1.37294 2.55469 1.33422 2.73979 1.33405H4.73979C5.06333 1.33086 5.37699 1.44543 5.6223 1.6564C5.86761 1.86737 6.02784 2.16035 6.07313 2.48072C6.15754 3.12076 6.31409 3.7492 6.53979 4.35405C6.62949 4.59266 6.6489 4.85199 6.59573 5.1013C6.54256 5.35061 6.41903 5.57946 6.23979 5.76072L5.39313 6.60738C6.34216 8.27641 7.7241 9.65835 9.39313 10.6074L10.2398 9.76072C10.4211 9.58147 10.6499 9.45795 10.8992 9.40478C11.1485 9.35161 11.4078 9.37102 11.6465 9.46072C12.2513 9.68642 12.8797 9.84297 13.5198 9.92738C13.8436 9.97307 14.1394 10.1362 14.3508 10.3857C14.5622 10.6352 14.6746 10.9538 14.6665 11.2807Z" stroke="#0B1215" strokeLinecap="round" strokeLinejoin="round" />
                    </g>
                    <defs>
                      <clipPath id="clip-phone">
                        <rect width="16" height="16" fill="white" />
                      </clipPath>
                    </defs>
                  </svg>
                  <span className={classes.contactSectionLabel}>{t('kiwi.infoModal.contact.phone')}</span>
                </div>
                <div className={classes.contactPhoneList}>
                  <div className={classes.contactPhoneRow}>
                    <span className={classes.contactPhoneNumber}>1-800-953-7392</span>
                    <span className={classes.contactPhoneDesc}>{t('kiwi.infoModal.contact.withinUS')}</span>
                  </div>
                  <div className={classes.contactPhoneRow}>
                    <span className={classes.contactPhoneNumber}>1-630-350-4551</span>
                    <span className={classes.contactPhoneDesc}>{t('kiwi.infoModal.contact.outsideUS')}</span>
                  </div>
                </div>
              </div>

              <div className={classes.contactSection}>
                <div className={classes.contactSectionHeader}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" className={classes.contactIcon}>
                    <path d="M2.66683 2.66602H13.3335C14.0668 2.66602 14.6668 3.26602 14.6668 3.99935V11.9993C14.6668 12.7327 14.0668 13.3327 13.3335 13.3327H2.66683C1.9335 13.3327 1.3335 12.7327 1.3335 11.9993V3.99935C1.3335 3.26602 1.9335 2.66602 2.66683 2.66602Z" stroke="#0B1215" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M14.6668 4L8.00016 8.66667L1.3335 4" stroke="#0B1215" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className={classes.contactSectionLabel}>{t('kiwi.infoModal.contact.contactForm')}</span>
                </div>
                <button
                  type="button"
                  className={classes.contactUsBtn}
                  onClick={() => window.open('https://www.visasignaturehotels.com/contact/rate', '_blank', 'noopener,noreferrer')}
                >
                  {t('kiwi.infoModal.tab.contactUs')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Photo gallery overlay */}
      {photosOpen && imageUrls.length > 0 && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          className={classes.photosOverlay}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target === e.currentTarget) setPhotosOpen(false);
          }}
        >
          <div className={classes.photosContent} onClick={(e) => e.stopPropagation()}>
            <div className={classes.photosHeader}>
              <h3 className={classes.photosTitle}>{data.title}</h3>
              <button
                type="button"
                className={classes.photosClose}
                onClick={(e) => {
                  e.stopPropagation();
                  setPhotosOpen(false);
                }}
                aria-label={t('kiwi.common.close')}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="#111827">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>
            <div className={classes.photosBody}>
              <div className={classes.photosThumbsScroll}>
                <div className={classes.photosThumbsGrid}>
                  {imageUrls.map((url, idx) => (
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                    <div
                      key={`gthumb-${idx}-${url}`}
                      className={`${classes.photosThumb} ${idx === photoIndex ? classes.photosThumbActive : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPhotoIndex(idx);
                      }}
                      role="button"
                      tabIndex={0}
                    >
                      <img src={url} alt={`${data.title} ${idx + 1}`} loading="lazy" />
                    </div>
                  ))}
                </div>
              </div>
              <div className={classes.photosMain}>
                <button
                  type="button"
                  className={`${classes.photosNav} ${classes.photosNavPrev}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToPrevPhoto();
                  }}
                  aria-label={t('kiwi.infoModal.previous')}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="15 18 9 12 15 6" />
                  </svg>
                </button>
                <img src={imageUrls[photoIndex]} alt={`${data.title} ${photoIndex + 1}`} className={classes.photosMainImg} />
                <button
                  type="button"
                  className={`${classes.photosNav} ${classes.photosNavNext}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    goToNextPhoto();
                  }}
                  aria-label={t('kiwi.infoModal.next')}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" fill="none" stroke="#111827" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Modals */}
      {visaModalOpen && <KiwiVisaBenefitsModal onClose={() => setVisaModalOpen(false)} t={t} />}
      {offersModalOpen && (
        <KiwiSpecialOffersModal
          hotelName={data.title}
          specialOffers={loadingDetail ? undefined : (propertyDetail?.specialOffers ?? [])}
          onClose={() => setOffersModalOpen(false)}
          t={t}
        />
      )}
      {roomRateModal && (
        <KiwiRoomRateModal
          roomTitle={roomRateModal.roomTitle}
          rate={roomRateModal.rate}
          currency={roomRateModal.currency}
          nightCount={nightCount}
          onClose={() => setRoomRateModal(undefined)}
          t={t}
          onBookNow={hotel.catalogUrl ? () => {
            setRoomRateModal(undefined);
            const base = hotel.catalogUrl.replace(/\/$/, '');
            const url = internalCheckIn && internalCheckOut
              ? `${base}/${internalCheckIn}/${internalCheckOut}/${internalAdults}/${internalChildren}/1`
              : base;
            window.open(url, '_blank', 'noopener,noreferrer');
          } : undefined}
        />
      )}
      {bookingModal && onCreateReservation && (
        <KiwiBookingModal
          hotelName={data.title}
          roomTitle={bookingModal.room.title}
          rate={bookingModal.rate}
          currency={bookingModal.currency}
          checkIn={internalCheckIn}
          checkOut={internalCheckOut}
          nightCount={nightCount}
          adults={internalAdults}
          children={internalChildren}
          propertyCode={hotel.code}
          roomCode={bookingModal.room.code}
          generateExternalId={generateExternalId || (() => `${hotel.code}-${Date.now()}`)}
          onClose={() => setBookingModal(undefined)}
          onCreateReservation={async (req) => {
            const res = await onCreateReservation(req);
            if (res.data && onReservationCreated) {
              onReservationCreated(res.data, req.external_id);
            }
            return res;
          }}
          onReservationFailed={onReservationFailed ? (msg) => onReservationFailed(hotel, bookingModal.room, bookingModal.rate, msg) : undefined}
          t={t}
        />
      )}
      {rateDetailInfo && (
        <KiwiRateDetailModal
          rate={rateDetailInfo.rate}
          currency={rateDetailInfo.currency}
          nightCount={nightCount}
          onClose={() => setRateDetailInfo(undefined)}
          t={t}
        />
      )}
    </div>,
    document.body,
  );
};

export default KiwiHotelInfoModal;